import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { addDays, addMonths, endOfMonth, endOfWeek, format, isSameMonth, startOfMonth, startOfWeek } from "date-fns"
import { CalendarDays, ChevronLeft, ChevronRight, List, Plus, Sparkles } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { fetchBookings } from "~/apis/bookings"
import { fetchProperties } from "~/apis/properties"
import { fetchPropertyNotes, createPropertyNote, deletePropertyNote } from "~/apis/propertyNotes"
import DayNoteDialog from "~/components/calendar/DayNoteDialog"
import { WEEK_OPTS, calendarBookings, cleansFrom, propertyColor, shortName, usePersistedState } from "~/lib/calendar"
import MonthGrid from "~/components/calendar/MonthGrid"
import Agenda from "~/components/calendar/Agenda"
import TodayPanel from "~/components/calendar/TodayPanel"
import BookingList from "~/components/BookingList"
import { PageSpinner } from "~/components/Spinner"
import routes from "~/routes"

const AGENDA_DAYS = 60

const PILL_ON = "border-border bg-card text-foreground/80"
const PILL_OFF = "border-transparent bg-muted text-muted-foreground line-through"

function FilterPill({ on, onClick, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-pressed={on}
      className={cn("flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium", on ? PILL_ON : PILL_OFF)}
    >
      {children}
    </button>
  )
}

function ViewToggle({ view, onChange }) {
  const option = (value, icon, label) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      aria-pressed={view === value}
      className={cn(
        "flex items-center gap-1 rounded-md px-2.5 py-1.5",
        view === value ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
      )}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div className="flex items-center rounded-lg bg-muted p-0.5 text-xs font-semibold">
      {option("calendar", <CalendarDays size={14} />, (
        <>
          <span className="hidden md:inline">Month</span>
          <span className="md:hidden">Agenda</span>
        </>
      ))}
      {option("list", <List size={14} />, "List")}
    </div>
  )
}

export default function Home() {
  // Coming back from a booking or clean lands on the month you were looking at.
  const [ month, setMonthState ] = useState(() => {
    try {
      const stored = sessionStorage.getItem("calendarMonth")
      if (stored) return startOfMonth(new Date(`${stored}-01T00:00:00`))
    } catch { /* storage unavailable */ }
    return startOfMonth(new Date())
  })
  const setMonth = (next) => {
    setMonthState(next)
    try { sessionStorage.setItem("calendarMonth", format(next, "yyyy-MM")) } catch { /* storage unavailable */ }
  }
  const [ notes, setNotes ] = useState([])
  const [ noteDay, setNoteDay ] = useState(null)
  const [ view, setView ] = usePersistedState("calendarView", "calendar")
  const [ filters, setFilters ] = usePersistedState("calendarFilters", { hiddenProperties: [], showCleans: true })
  const [ properties, setProperties ] = useState(null)
  const [ bookings, setBookings ] = useState(null)
  const [ error, setError ] = useState(null)

  useEffect(() => {
    fetchProperties().then(setProperties).catch((err) => setError(err.message))
  }, [])

  // One superset fetch covers both the month grid and the 60-day agenda. The
  // extra day at the front keeps a booking that checks out today in the set
  // (the API's overlap test is exclusive of ends_on).
  const range = useMemo(() => {
    const today = new Date()
    const gridStart = startOfWeek(startOfMonth(month), WEEK_OPTS)
    const gridEnd = endOfWeek(endOfMonth(month), WEEK_OPTS)
    const agendaEnd = addDays(today, AGENDA_DAYS + 1)
    return {
      from: format(addDays(gridStart < today ? gridStart : today, -1), "yyyy-MM-dd"),
      to: format(gridEnd > agendaEnd ? gridEnd : agendaEnd, "yyyy-MM-dd"),
    }
  }, [ month ])

  const loadNotes = () => fetchPropertyNotes(range.from, range.to).then(setNotes).catch(() => {})
  useEffect(() => { loadNotes() }, [ range.from, range.to ])

  useEffect(() => {
    let stale = false
    fetchBookings(range.from, range.to)
      .then((data) => !stale && setBookings(data))
      .catch((err) => !stale && setError(err.message))
    return () => {
      stale = true
    }
  }, [ range.from, range.to ])

  // A property pill shows just that property; clicking it again brings the
  // rest back. (Stored as "hidden" ids so old saved filters still work.)
  const toggleProperty = (id) => {
    const others = (properties || []).map((p) => p.id).filter((p) => p !== id)
    const alreadySolo = others.length > 0 && others.every((p) => filters.hiddenProperties.includes(p)) && !filters.hiddenProperties.includes(id)
    setFilters({ ...filters, hiddenProperties: alreadySolo ? [] : others })
  }

  // Apply the property / cleans toggles once, so every view shows the same slice.
  const visible = useMemo(() => {
    if (!properties || !bookings) return null
    const shown = (propertyId) => !filters.hiddenProperties.includes(propertyId)
    const all = calendarBookings(bookings)
    const live = all.filter((b) => shown(b.property_id))
    return {
      all,
      properties: properties.filter((p) => shown(p.id)),
      bookings: live,
      cleans: filters.showCleans ? cleansFrom(live) : [],
    }
  }, [ properties, bookings, filters ])

  const showingAll = filters.hiddenProperties.length === 0

  const isCurrentMonth = isSameMonth(month, new Date())

  return (
    <div className="mx-auto max-w-7xl px-4 py-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-bold">Calendar</h1>
        <ViewToggle view={view} onChange={setView} />
        <div className="flex-1" />

        <div className="mr-1 flex flex-wrap items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className={cn("h-7 rounded-full px-2.5 text-xs", showingAll && filters.showCleans && "border-brand/40 bg-brand/10 text-foreground")}
            onClick={() => setFilters({ hiddenProperties: [], showCleans: true })}
            title="Show every property and every clean"
          >
            Show all
          </Button>
          {properties?.map((p) => {
            const on = !filters.hiddenProperties.includes(p.id)
            return (
              <FilterPill key={p.id} on={on} onClick={() => toggleProperty(p.id)} title={on && filters.hiddenProperties.length > 0 ? "Click to show all properties" : `Click to show only ${shortName(p)}`}>
                <span className="size-2.5 rounded-full" style={{ background: on ? propertyColor(p) : "#d6d3d1" }} />
                {shortName(p)}
              </FilterPill>
            )
          })}
          <FilterPill
            on={filters.showCleans}
            onClick={() => setFilters({ ...filters, showCleans: !filters.showCleans })}
            title={filters.showCleans ? "Click to hide cleans" : "Click to show cleans"}
          >
            <Sparkles size={11} /> Cleans
          </FilterPill>
        </div>

        {view === "calendar" && (
          <div className="hidden items-center gap-1 md:flex">
            <Button variant="ghost" size="icon-sm" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">
              <ChevronLeft />
            </Button>
            <span className="w-32 text-center text-sm font-semibold">{format(month, "MMMM yyyy")}</span>
            <Button variant="ghost" size="icon-sm" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">
              <ChevronRight />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setMonth(startOfMonth(new Date()))} disabled={isCurrentMonth}>
              Today
            </Button>
          </div>
        )}

        <Button asChild size="sm" className="bg-brand text-brand-foreground hover:bg-brand/90 md:hidden">
          <Link to={routes.newBooking}>
            <Plus /> Booking
          </Link>
        </Button>
      </div>

      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

      {properties && bookings && <TodayPanel properties={properties} bookings={bookings} />}

      {!visible ? (
        <PageSpinner />
      ) : view === "list" ? (
        <BookingList properties={visible.properties} />
      ) : (
        <>
          <div className="hidden md:block">
            <MonthGrid
              month={month}
              properties={properties}
              bookings={visible.all}
              cleans={cleansFrom(visible.all)}
              notes={notes.filter((n) => !filters.hiddenProperties.includes(n.property_id))}
              hiddenProperties={filters.hiddenProperties}
              showCleans={filters.showCleans}
              onAddNote={(day) => setNoteDay(day)}
              onDeleteNote={async (note) => { if (window.confirm("Remove this note?")) { await deletePropertyNote(note.id); loadNotes() } }}
            />
            {noteDay && (
              <DayNoteDialog
                day={noteDay}
                properties={properties}
                onClose={() => setNoteDay(null)}
                onSave={async (payload) => { await createPropertyNote(payload); setNoteDay(null); loadNotes() }}
              />
            )}
          </div>
          <div className="md:hidden">
            <Agenda properties={visible.properties} bookings={visible.bookings} cleans={visible.cleans} />
          </div>
        </>
      )}
    </div>
  )
}
